<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
                        <li class="pr-3"><a class="cursor-pointer">Configuration</a></li>
                        <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
                        <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Labels</a></li>
                    </ul>

                    <h3>Manage Labels</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewLabel" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Label</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="labels"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name', 'description']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Name or Description"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No labels found.
                        </template>

                        <template #loading>
                            Loading labels. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v" class="p-button-sm p-button-text p-button-rounded m-0"
                                        @click="$refs['menu' + index].toggle($event)"/>
                                <Menu
                                    :ref="'menu' + index"
                                    :model="getFilteredItems(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Label {{ this.label.name }}</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="label.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="label.description" required="true"/>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="label.status" :options="statusTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Status"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :disabled="!canUpdateLabel" :loading="submitted"
                            @click="updateLabel"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Label {{ this.label.name }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Label?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" :loading="submitted" @click="deleteLabel"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Label</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="newLabel.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <InputText id="description" v-model.trim="newLabel.description" required="true"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" :disabled="!canCreateLabel" :loading="submitted"
                            @click="createLabel"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Clients for Label {{ this.label.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callReset" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Back to Manage Labels</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="clientLabels"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name', 'description']">

                        <template #empty>
                            No clients found for label.
                        </template>

                        <template #loading>
                            Loading clients for label. Please wait.
                        </template>

                        <Column field="clientName" header="Client Name" :sortable="true"/>
                        <Column field="created" header="Created" :sortable="true"/>

                    </DataTable>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import CompanyService from "@/service/CompanyService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import LabelService from "@/service/LabelService";
import ClientLabelService from "@/service/ClientLabelService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'name', header: 'Name', sortable: true},
            {field: 'description', header: 'Description', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'id', header: 'ID', sortable: true},
            {field: 'name', header: 'Name', sortable: true},
            {field: 'description', header: 'Description', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'active', header: 'Active'},
            {field: 'inactive', header: 'Inactive'}
        ];

    },
    async mounted() {
        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            label: {},
            labels: null,
            newLabel: {},
            originalLabels: null,
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            clientLabels: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ]
        }
    },
    watch: {
        labels() {
            this.filteredItemsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    computed: {
        canCreateLabel() {
            if ((this.newLabel.name) && (this.newLabel.description) ) {
                return true;
            } else {
                return false;
            }
        },
        canUpdateLabel() {
            if ((this.label.name) && (this.label.description) && (this.label.status)) {
                return true;
            } else {
                return false;
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigurationData, companyConfigurationData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id),
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                ]);

                this.userConfiguration = userConfigurationData.data;
                this.companyConfiguration = companyConfigurationData.data;

                await this.loadData();

                this.loading = false;

            } catch (e) {
                console.error("[ConfigurationLabels][mounted] Error fetching additional data = ", e);
            }

            console.log("[ConfigurationLabels][mounted] this.labels = ", this.labels);
            console.log("[ConfigurationLabels][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[ConfigurationLabels][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        async loadData() {
            this.submitted = true;
            try {
                const [labelData] = await Promise.all([
                    LabelService.getCompanyLabels(this.profile.companyId)
                ]);

                this.labels = labelData.data;
                this.originalLabels = [...this.labels];
                console.log("[ConfigurationLabels][loadData] labelData.data = ", labelData.data);
            } catch (error) {
                console.error("[ConfigurationLabels][loadData] Error loading labels:", error);
                this.labels = [];
                this.originalLabels = [];
            } finally {
                this.submitted = false;
            }
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'name': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'description': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        async restoreToOriginal() {
            await this.loadData();
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callNewLabel() {
            this.activeTab = 4;
            this.newLabel = {};
        },
        callUpdateLabel(data) {
            this.activeTab = 2;
            this.label = data;
        },
        callDeleteLabel(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.label = data;
        },
        async callViewClients(data) {
            this.activeTab = 5;
            this.label = data;

            const [clientLabelData] = await Promise.all([
                ClientLabelService.getClientsForLabel(this.label.id)
            ]);

            this.clientLabels = clientLabelData.data;

            console.log('[ConfigurationLabels][callViewClients] this.clientLabels = ', this.clientLabels);
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.labels !== null) {

                if (isActive) {
                    this.labels = this.originalLabels.filter(label => label.status === 'A');
                } else if (isInactive) {
                    this.labels = this.originalLabels.filter(label => label.status === 'I');
                } else {
                    this.labels = this.originalLabels;
                }

            } else {
                this.labels = [];
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateLabel(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteLabel(data);
                }
            });

            items.push({
                label: 'View Clients',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callViewClients(data);
                }
            });

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        async updateLabel() {
            this.submitted = true;

            if (this.label.id) {

                try {
                    var labelDto = {
                        name: this.label.name,
                        description: this.label.description,
                        status: this.label.status
                    };

                    const response = await LabelService.updateLabel(this.label.id, labelDto);
                    console.log('[ConfigurationLabels][updateLabel] Response = ', response);

                    await this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated label',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ConfigurationLabels][updateLabel] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update label. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deleteLabel() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.label.id) {

                    try {
                        const response = await LabelService.deleteLabel(this.label.id);
                        console.log('[ConfigurationLabels][deleteLabel] Response = ', response);

                        await this.loadData();

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted label',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[ConfigurationLabels][deleteLabel] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete label. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async createLabel() {
            this.submitted = true;

            if (this.newLabel.name) {

                try {
                    var newLabelDto = {
                        name: this.newLabel.name,
                        description: this.newLabel.description,
                        status: 'A'
                    };

                    const response = await LabelService.createLabel(this.profile.companyId, newLabelDto);
                    console.log('[ConfigurationLabels][createLabel] Response = ', response);

                    await this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created label',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ConfigurationLabels][createLabel] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create label. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-A {
        background: #9da5d9;
        color: #080c61;
    }

    &.status-I {
        background: #B3E5FC;
        color: #23547B;
    }

}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
